import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  DeleteReplyTemplateValidationSchema,
  TDeleteReplyTemplateValidation,
  useDeleteReplyTemplateMutation,
} from '../../../api/delete-reply-template.api';
import { GET_REPLY_TEMPLATES_QUERY_KEY } from '../../../api/get-reply-templates.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { useCustomToast } from '@/hooks/use-custom-toast';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';

export type TDeleteReplyTemplateModalType = 'DeleteReplyTemplateModal';
export interface IDeleteReplyTemplateModalProps {
  replyTemplateId: string;
}

const DeleteReplyTemplateModal = memo(({ replyTemplateId }: IDeleteReplyTemplateModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteReplyTemplateModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('DeleteReplyTemplateModal');
  }, [caseModuleModalRootActions]);

  const form = useForm<TDeleteReplyTemplateValidation>({
    resolver: zodResolver(DeleteReplyTemplateValidationSchema),
    mode: 'onChange',
    defaultValues: {
      templateId: replyTemplateId || '',
    },
  });

  useEffect(() => {
    if (replyTemplateId) {
      form.reset({
        templateId: replyTemplateId || '',
      });
    }
  }, [replyTemplateId, form]);

  const mutationDeleteReplyTemplate = useDeleteReplyTemplateMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error delete reply template, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_REPLY_TEMPLATES_QUERY_KEY] });
        caseModuleModalRootActions.closeModal('DeleteReplyTemplateModal');
        showToast({
          message: 'Reply template deleted successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitDeleteReplyTemplate = useCallback(
    (data: TDeleteReplyTemplateValidation) => {
      mutationDeleteReplyTemplate.mutate({
        templateId: data?.templateId || '',
      });
    },
    [mutationDeleteReplyTemplate]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('DeleteReplyTemplateModal');
  }, [caseModuleModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteReplyTemplateModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Are you sure you want to delete this template?</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitDeleteReplyTemplate)(e);
          }}
          id="delete-reply-template-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">
            This template will be permanently deleted and can no longer be used by agents.
          </span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="delete-reply-template-form"
          type="submit"
          disabled={mutationDeleteReplyTemplate.isPending}
        >
          {mutationDeleteReplyTemplate.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteReplyTemplateModal.displayName = 'DeleteReplyTemplateModal';

export default DeleteReplyTemplateModal;
